import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PaymentGatewayService } from '../services';
import { PaymentGatewayProviderActions } from '../code/payment-gateways';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, forkJoin, merge, of } from 'rxjs';
import { filter, map, scan, switchMap } from 'rxjs/operators';
import { genericSearch, LoggerService } from '@adreno-common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  BlackbaudFetchTokenRequest,
  BlackbaudStoreTokenRequest,
  BlackbaudCreateRevenuePaymentRequest,
  EwayOnceOffRequest,
  EwayStoreTokenRequest,
  EwayUpdateTokenRequest,
} from '../models';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-payment-gateway',
  templateUrl: './payment-gateway.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentGatewayComponent implements OnInit {
  @Input() additionalValues?: { [key: string]: string | number | undefined };
  @Input() campaignId?: number;
  @Input() providerAction?: PaymentGatewayProviderActions;

  private submitting$ = new BehaviorSubject<boolean>(false);

  formGroup!: FormGroup;
  genericSearch = genericSearch;
  viewModel$!: Observable<ViewModel>;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private pgSvc: PaymentGatewayService,
    private domSanitizer: DomSanitizer,
    private logger: LoggerService,
    private toastSvc: ToastrService
  ) {}

  private buildBlackbaudSrcDoc(publicKey: string, token: string) {
    return this.domSanitizer.bypassSecurityTrustHtml(`
<html>
  <body>
    <script src='https://payments.blackbaud.com/Checkout/bbCheckout.2.0.js'></script>  
    <script>
      var transactionData = {
        'card_token': '${token}',
        'key': '${publicKey}',
        'is_offline': true,
      };
      
      var targetOrigins = [
          "https://agent.unity4.com",
          "https://connect.unity4.com",
          "https://app-teleworker-unity4-staging.azurewebsites.net",
          "https://localhost:4201",
      ];
      
      function postResult(type, event) {
          var msg = {
              type: "blackbaud." + type,
              detail: event.detail
          };
          for (var i = 0; i < targetOrigins.length; i++) {
              window.parent.postMessage(msg, targetOrigins[i]);
          }
      }
      
      document.addEventListener('checkoutReady', function (event) {
          //do nothing
      });
      
      document.addEventListener('checkoutLoaded', function (event) {
          //do nothing
      });
      
      document.addEventListener('checkoutComplete', function (event) {
        postResult('checkoutComplete', event);
      });
      
      document.addEventListener('checkoutCancel', function (event) {
        postResult('checkoutCancel', event);
      });
      
      document.addEventListener('checkoutError', function (event) {
        postResult('checkoutError', event);
      });
      
      Blackbaud_OpenUpdateCardForm(transactionData);
    </script>
  </body>
</html>`);
  }

  private closeModal(result: any) {
    this.submitting$.next(false);
    this.activeModal.close(result);
    window.removeEventListener('message', this.onMessage);
  }

  private initFormGroup() {
    this.formGroup = this.fb.group(
      {
        n: this.fb.control(this.additionalValues?.name, [
          Validators.required,
          Validators.maxLength(50),
        ]),
        cn: this.fb.control(undefined, [
          Validators.required,
          Validators.maxLength(19),
        ]),
        em: this.fb.control(undefined, [Validators.required]),
        ey: this.fb.control(undefined, [Validators.required]),
        v: this.fb.control(
          undefined,
          this.captureCCV ? [Validators.required, Validators.maxLength(4)] : []
        ),
        ct: this.fb.control(
          undefined,
          this.captureCardType ? [Validators.required] : []
        ),
      },
      {
        validators: [expiryValidator],
      }
    );
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      of(this.providerName).pipe(map((provider) => ({ provider }))),
      of(this.paymentMethod).pipe(map((paymentMethod) => ({ paymentMethod }))),
      of(this.captureCCV).pipe(map((captureCCV) => ({ captureCCV }))),
      of(this.years).pipe(map((years) => ({ years }))),
      of(this.captureCardType).pipe(
        map((captureCardType) => ({ captureCardType }))
      ),
      of(this.cardTypes).pipe(map((cardTypes) => ({ cardTypes }))),
      of(this.paymentMethod).pipe(
        filter((paymentMethod) => paymentMethod == 'blackbaud-update-token'),
        switchMap(() =>
          forkJoin([
            this.pgSvc.blackbaudPublicKey(this.campaignId!),
            this.pgSvc.blackbaudFetchToken({
              campaignId: this.campaignId!,
              constituentId: this.additionalValues?.constituentId,
              giftImportId: this.additionalValues?.giftImportId,
            } as BlackbaudFetchTokenRequest),
          ])
        ),
        map(([publickKeyVm, fetchTokenVm]) => ({
          blackbaudSrcDoc: this.buildBlackbaudSrcDoc(
            publickKeyVm.publicKey,
            fetchTokenVm.token
          ),
        }))
      ),
      this.submitting$.pipe(map((submitting) => ({ submitting })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  private get cardTypes(): string[] | undefined {
    const { provider, action } = this.providerAction || {};
    if (provider == 'blackbaud' && (action == 'token' || action == 'instant'))
      return ['MasterCard', 'Visa', 'AMEX'];
    return undefined;
  }

  private get captureCCV(): boolean | undefined {
    const { provider, action } = this.providerAction || {};
    if (provider == 'eway' && action == 'onceoff') return true;
    if (provider == 'blackbaud' && action == 'instant') return true;
    return undefined;
  }

  private get captureCardType(): boolean | undefined {
    const { provider, action } = this.providerAction || {};
    if (provider == 'blackbaud' && (action == 'token' || action == 'instant'))
      return true;
    return undefined;
  }

  private get paymentMethod(): PaymentMethod | undefined {
    const { provider, action } = this.providerAction || {};
    if (provider == 'blackbaud' && action == 'token') return 'cc';
    if (provider == 'blackbaud' && action == 'update-token')
      return 'blackbaud-update-token';
    if (provider == 'blackbaud' && action == 'instant') return 'cc';
    if (provider == 'eway' && action == 'token') return 'cc';
    if (provider == 'eway' && action == 'onceoff') return 'cc';
    if (provider == 'eway' && action == 'update-token') return 'cc';
    return undefined;
  }

  private get providerName(): string {
    const { provider, action } = this.providerAction || {
      provider: undefined,
      action: undefined,
    };
    if (provider == 'blackbaud') return 'Blackbaud';
    if (provider == 'eway') return 'Eway';
    return provider || 'undefined';
  }

  private get years(): number[] {
    const nowYr = new Date().getFullYear();
    return new Array(10).fill(nowYr).map((yr, idx) => yr + idx);
  }

  close() {
    this.closeModal('agent-closed');
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.initViewModel();
    if (this.paymentMethod == 'blackbaud-update-token') {
      window.addEventListener('message', this.onMessage);
    }
  }

  onMessage = (message: MessageEvent<any>) => {
    if (message.origin != 'https://localhost:5000')
      this.logger.debug('PaymentGatewayComponent', 'onMessage', {
        origin: message.origin,
        data: message.data,
      })();
    if (message.data?.type?.startsWith('blackbaud')) {
      this.logger.info('PaymentGatewayComponent', 'onMessage', {
        origin: message.origin,
        data: message.data,
      })();
      this.closeModal(message.data);
    }
  };

  process() {
    if (!this.formGroup.valid) return;
    const {
      cn: number,
      n: name,
      em: expiryMonth,
      ey: expiryYear,
      ct: cardType,
      v: cvn,
    } = this.formGroup.value;

    const { provider, action } = this.providerAction || {};
    this.submitting$.next(true);

    if (provider == 'blackbaud' && action == 'token') {
      this.pgSvc
        .blackbaudStoreToken({
          campaignId: this.campaignId!,
          number,
          name,
          expiryMonth,
          expiryYear,
          cardType,
        } as BlackbaudStoreTokenRequest)
        .subscribe((result) => this.closeModal(result));
      return;
    }

    if (provider == 'blackbaud' && action == 'instant') {
      this.pgSvc
        .blackbaudCreateRevenuePayment({
          campaignId: this.campaignId!,
          number,
          name,
          expiryMonth,
          expiryYear,
          cardType,
          amount: +(this.additionalValues?.amount || 0),
          recordId: this.additionalValues?.recordId,
          cvn,
        } as BlackbaudCreateRevenuePaymentRequest)
        .subscribe((result) => this.closeModal(result));
      return;
    }

    if (provider == 'eway' && action == 'token') {
      this.pgSvc
        .ewayStoreToken({
          campaignId: this.campaignId!,
          recordId: this.additionalValues?.recordId,
          first: this.additionalValues?.first,
          last: this.additionalValues?.last,
          number,
          name,
          expiryMonth,
          expiryYear,
        } as EwayStoreTokenRequest)
        .subscribe((result) => this.closeModal(result));
      return;
    }
    if (provider == 'eway' && action == 'onceoff') {
      this.pgSvc
        .ewayOnceOff({
          campaignId: this.campaignId!,
          recordId: this.additionalValues?.recordId,
          first: this.additionalValues?.first,
          last: this.additionalValues?.last,
          number,
          name,
          expiryMonth,
          expiryYear,
          amount: +(this.additionalValues?.amount || 0),
          cvn,
        } as EwayOnceOffRequest)
        .subscribe((result) => this.closeModal(result));
      return;
    }
    if (provider == 'eway' && action == 'update-token') {
      this.pgSvc
        .ewayUpdateToken({
          campaignId: this.campaignId!,
          recordId: this.additionalValues?.recordId,
          first: this.additionalValues?.first,
          last: this.additionalValues?.last,
          number,
          name,
          expiryMonth,
          expiryYear,
          tokenId: +(this.additionalValues?.tokenId || 0),
        } as EwayUpdateTokenRequest)
        .subscribe((result) => this.closeModal(result));
      return;
    }

    this.submitting$.next(false);
    this.toastSvc.error(`Unknown provider/action "${provider}/${action}"`);
    this.logger.error(
      'PaymentGatewayComponent.processToken',
      'Unknown provider/action',
      provider,
      action
    )();
  }
}

function expiryValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const expiryMonth = control.get('em')?.value;
    const expiryYear = control.get('ey')?.value;
    if (!expiryMonth || !expiryYear) return null;

    const mth = new Date();
    mth.setDate(1);
    mth.setHours(0, 0, 0, 0);

    const expiry = new Date(expiryYear, expiryMonth, 1);

    return expiry < mth ? { validExpiryValidator: true } : null;
  };
}

interface ViewModel {
  blackbaudSrcDoc?: SafeHtml;
  captureCCV?: boolean;
  captureCardType?: boolean;
  cardTypes?: string[];
  paymentMethod?: PaymentMethod;
  provider?: string;
  submitting: boolean;
  years?: number[];
}

type PaymentMethod = 'cc' | 'dd' | 'blackbaud-update-token';
